import { navigate } from "gatsby"
import React from "react"

type Ctx = {
  isDirty: boolean
  nextUrl: null | string
  requestNavigate: (nextUrl: string) => void
  setDirty: (v: boolean) => void
  setNextUrl: (url: null | string) => void
}

export const NavigationGuardCtx = React.createContext<Ctx | null>(null)

export function NavigationGuardProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [isDirty, setDirty] = React.useState(false)
  const [nextUrl, setNextUrl] = React.useState<null | string>(null)

  // For programmatic navigations
  const requestNavigate = React.useCallback(
    (url: string) => {
      if (!isDirty) return navigate(url)
      setNextUrl(url) // Open modal on consumer
    },
    [isDirty]
  )

  // Global click capture for all <a>
  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      // Only left-click without modifiers
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      )
        return

      // Walk up to <a>
      let el = e.target as HTMLElement | null
      while (el && el.tagName !== "A") el = el.parentElement
      const a = el as HTMLAnchorElement | null
      if (!a || a.dataset.noGuard != null) return

      // Same-origin internal link?
      const href = a.getAttribute("href") || ""
      const isInternal =
        href.startsWith("/") || href.startsWith(window.location.origin)
      if (!isInternal) return

      if (isDirty) {
        e.preventDefault()
        setNextUrl(href) // Show modal; on confirm, call navigate(href)
      }
    }

    document.addEventListener("click", onClick, true) // Capture phase
    return () => document.removeEventListener("click", onClick, true)
  }, [isDirty])

  // Hard refresh / tab close
  React.useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return
      e.preventDefault()
      e.returnValue = "" // Required for some browsers
    }
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => window.removeEventListener("beforeunload", onBeforeUnload)
  }, [isDirty])

  // Back/forward buttons
  React.useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      if (!isDirty) return
      e.preventDefault()
      // We can’t read the *next* URL directly; reconstruct from location:
      const next =
        window.location.pathname + window.location.search + window.location.hash
      setNextUrl(next) // Open modal; if confirmed call navigate(next)
      history.go(1) // Push user back to current until they confirm
    }
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  }, [isDirty])

  const value = React.useMemo(
    () => ({
      isDirty,
      nextUrl,
      requestNavigate,
      setDirty,
      setNextUrl,
    }),
    [isDirty, requestNavigate, nextUrl]
  )

  return (
    <NavigationGuardCtx.Provider value={value}>
      {children}
    </NavigationGuardCtx.Provider>
  )
}
